import { Controller } from "@hotwired/stimulus"
import ChartRequestQueue from "../lib/chart_request_queue"
// Chart.js and ChartDataLabels are vendored globals loaded by application/_head.html.erb

// Async Chart Controller with Lazy Loading and Rate Limiting
// 
// This controller loads charts asynchronously only when they enter the viewport,
// reducing server load when pages have many charts (like classroom rosters).
// Charts remain cached after loading for fast subsequent access.
//
// Features:
// - Intersection Observer for viewport detection
// - Request queuing to prevent server overload (429 errors)
// - Exponential backoff retry for failed requests
// - Loading/error/success states
// - Proper cleanup on disconnect
export default class extends Controller {
  static targets = ["canvas", "loading", "error", "retryButton"]
  static values = { url: String, kind: String }

  connect() {
    this.chartLoaded = false
    this.chartFailed = false
    // INTENT: Listen for custom force-load events from print_report_controller or other managers.
    // EXPECTED OUTCOME: Triggers immediate chart loading without requiring viewport scrolling when printing.
    this.onForceLoad = () => this.forceLoad()
    this.element.addEventListener("async-chart:force-load", this.onForceLoad)
    // INTENT: Setup intersection observer so web view charts strictly load as user scrolls them into view.
    // EXPECTED OUTCOME: Normal web view defers chart fetching until scrolled into viewport.
    this.setupIntersectionObserver()
  }

  setupIntersectionObserver() {
    // Create intersection observer to detect when chart enters viewport
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.chartLoaded && !this.chartFailed) {
          this.loadChart()
        }
      })
    }, {
      // Load when chart is 50px from entering viewport
      rootMargin: '50px',
      // Trigger when at least 10% of the chart is visible
      threshold: 0.1
    })

    // Start observing this chart element
    this.observer.observe(this.element)
  }

  // Method to force immediate loading (can be called from other controllers or event handlers)
  forceLoad() {
    // INTENT: Prevent repeated forceLoad polling from retrying an errored chart endpoint infinitely.
    // EXPECTED OUTCOME: Only un-attempted, non-fetching, non-errored charts trigger loadChart.
    // CAVEAT/TRAP: If chart is already loaded or failed, notify event listeners directly without repeating XHR requests.
    if (!this.chartLoaded && !this.isFetching && !this.chartFailed) {
      this.loadChart()
    } else if (this.chartLoaded) {
      this.element.dispatchEvent(new CustomEvent("async-chart:loaded", { detail: { success: true } }))
    } else if (this.chartFailed) {
      this.element.dispatchEvent(new CustomEvent("async-chart:loaded", { detail: { success: false } }))
    }
  }


  async loadChart() {
    // Prevent multiple concurrent loads
    if (this.chartLoaded || this.isFetching) {
      return
    }

    this.isFetching = true
    this.showLoading()

    try {
      // Use the request queue to manage concurrent chart loads
      const chartData = await ChartRequestQueue.enqueue(async () => {
        const response = await fetch(this.urlValue, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          // Create error object with status for proper 429 handling
          const error = new Error(`HTTP error! status: ${response.status}`)
          error.status = response.status
          if (response.status === 403) {
            error.message = 'Access Forbidden (403)'
          }
          throw error
        }

        const chartData = await response.json()

        if (chartData.error) {
          throw new Error(chartData.error)
        }

        return chartData
      }, {
        maxRetries: 5,      // Retry up to 5 times for 429s
        baseDelay: 1000,    // Start with 1 second delay
        maxDelay: 10000     // Cap at 10 seconds
      })

      this.renderChart(chartData)
      this.showChart()
      this.chartLoaded = true
      this.isFetching = false

      // Stop observing once chart is loaded
      if (this.observer) {
        this.observer.unobserve(this.element)
      }

      // INTENT: Dispatch custom event so managers (like print_report_controller) can track chart loading completion.
      // EXPECTED OUTCOME: print_report_controller receives async-chart:loaded and updates progress.
      this.element.dispatchEvent(new CustomEvent("async-chart:loaded", { detail: { success: true } }))

    } catch (error) {
      console.error('Failed to load chart after retries:', error)

      // Manually report the handled error to AppSignal so we catch it
      if (window.appsignal && typeof window.appsignal.sendError === 'function') {
        window.appsignal.sendError(error)
      }

      this.showError()
      // Reset flags so manual retry can work
      this.chartLoaded = false
      this.isFetching = false

      // INTENT: Dispatch completion event on error so print manager progresses without hanging indefinitely.
      this.element.dispatchEvent(new CustomEvent("async-chart:loaded", { detail: { success: false, error } }))
    }
  }

  renderChart(chartData) {
    // Find the canvas element within the container
    const canvas = this.canvasTarget.querySelector('canvas')

    // Extract data and options from the response
    const { data, options } = chartData

    // Add plugins based on chart type and options
    let plugins = []

    // Add data labels plugin if requested in options
    if (options.plugins?.datalabels?.display) {
      plugins.push(window.ChartDataLabels)

      // Add formatter function for datalabels
      options.plugins.datalabels.formatter = (value, context) => {
        return value > 0 ? value : ''
      }
    }

    // Add quadrant background for scatter charts
    if (this.kindValue === 'scatter' && options.chartType !== 'bullseye_scatter') {
      plugins.push(this.quadrantBackgroundPlugin)
    }

    // Apply progressive opacity for preparedness journey
    if (options.chartType === 'preparedness_scatter') {
      this.applyProgressiveOpacity(data)
    }

    // Determine Chart.js chart type
    let chartType = this.kindValue
    if (options.chartType === 'progress_column') {
      chartType = 'bar'
    }

    if (options.chartType === 'accuracy_bar') {
      chartType = 'bar'
      options.plugins = options.plugins || {}
      options.plugins.tooltip = {
        callbacks: {
          label: (context) => {
            return `${context.parsed.y}% Accuracy`
          }
        }
      }
      if (options.scales && options.scales.y) {
        options.scales.y.ticks = {
          callback: (value) => {
            return `${value}%`
          }
        }
      }
    }

    // Store the chart instance on the controller for potential cleanup
    this.chart = new Chart(canvas, {
      type: chartType,
      data: data,
      options: options,
      plugins: plugins
    })
  }

  // Apply progressive opacity to preparedness journey points
  applyProgressiveOpacity(data) {
    if (data.datasets && data.datasets[0] && data.datasets[0].data) {
      const dataset = data.datasets[0]
      const points = dataset.data

      // Convert base colors to rgba with opacity
      dataset.backgroundColor = points.map((point) => {
        const [r, g, b] = this.hexToRgb(point.base_color)
        return `rgba(${r}, ${g}, ${b}, ${point.opacity})`
      })

      dataset.borderColor = points.map((point) => {
        const [r, g, b] = this.hexToRgb(point.base_color)
        return `rgba(${r}, ${g}, ${b}, ${point.opacity})`
      })
    }
  }

  // Convert hex color to RGB values
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0]
  }

  get quadrantBackgroundPlugin() {
    return {
      id: 'quadrantBackground',
      beforeDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d')
        const chartArea = chart.chartArea
        const xScale = chart.scales.x
        const yScale = chart.scales.y
        if (!xScale || !yScale || !chartArea) return;
        ctx.save()
        const x50 = xScale.getPixelForValue(50)
        const y85 = yScale.getPixelForValue(85)

        // High Potential (top left) - Light Green with transparency
        ctx.fillStyle = 'rgba(102, 197, 171, 0.2)'
        ctx.fillRect(chartArea.left, chartArea.top, x50 - chartArea.left, y85 - chartArea.top)

        // On Track (top right) - Green with transparency  
        ctx.fillStyle = 'rgba(0, 158, 115, 0.2)'
        ctx.fillRect(x50, chartArea.top, chartArea.right - x50, y85 - chartArea.top)

        // Needs Engagement (bottom left) - Light Red with transparency
        ctx.fillStyle = 'rgba(230, 158, 102, 0.2)'
        ctx.fillRect(chartArea.left, y85, x50 - chartArea.left, chartArea.bottom - y85)

        // Needs Support (bottom right) - Red with transparency
        ctx.fillStyle = 'rgba(213, 94, 0, 0.2)'
        ctx.fillRect(x50, y85, chartArea.right - x50, chartArea.bottom - y85)
        ctx.restore()
      }
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove('hidden')
    if (this.hasCanvasTarget) this.canvasTarget.classList.add('hidden')
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden')
  }

  showChart() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden')
    if (this.hasCanvasTarget) this.canvasTarget.classList.remove('hidden')
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden')
  }

  showError() {
    this.chartFailed = true
    if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden')
    if (this.hasCanvasTarget) this.canvasTarget.classList.add('hidden')
    if (this.hasErrorTarget) this.errorTarget.classList.remove('hidden')
  }

  retry() {
    // Reset the loaded and failed flags and try again
    this.chartLoaded = false
    this.chartFailed = false
    this.loadChart()
  }


  // Debug method to check queue status (can be called from browser console)
  getQueueStatus() {
    return ChartRequestQueue.getStatus()
  }

  disconnect() {
    // INTENT: Clean up force-load event listener to prevent duplicate handlers on element reconnect.
    if (this.onForceLoad) {
      this.element.removeEventListener("async-chart:force-load", this.onForceLoad)
      this.onForceLoad = null
    }

    // Clean up intersection observer
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    // Clean up chart instance when controller is disconnected
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }
}